import React from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, ArrowRight, X } from "lucide-react";

/* ================= OVERLAY ================= */

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.78);
  backdrop-filter: blur(12px);
  z-index: 3000;
  display: grid;
  place-items: center;
`;

/* ================= MODAL ================= */

const Modal = styled(motion.div)`
  width: 100%;
  max-width: 520px;
  background: linear-gradient(
    180deg,
    rgba(12, 18, 40, 0.98),
    rgba(6, 10, 24, 0.98)
  );
  border-radius: 28px;
  padding: 44px 38px 38px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 50px 160px rgba(0, 0, 0, 0.7);
  position: relative;
  text-align: center;
`;

/* ================= CLOSE ================= */

const Close = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
`;

/* ================= ICON ================= */

const IconWrap = styled.div`
  width: 78px;
  height: 78px;
  border-radius: 26px;
  display: grid;
  place-items: center;
  margin: 0 auto 28px;
  color: #fff;

  background: linear-gradient(135deg, #22c55e, #3b82f6);
  box-shadow: 0 0 80px rgba(59, 130, 246, 0.55);
`;

/* ================= TEXT ================= */

const Title = styled.h2`
  font-size: 30px;
  font-weight: 1000;
  line-height: 1.15;
  color: #fff;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #22c55e, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Sub = styled.p`
  margin-top: 18px;
  font-size: 16.5px;
  line-height: 1.7;
  color: #cbd5e1;
`;

/* ================= BULLETS ================= */

const Points = styled.div`
  margin: 30px 0 36px;
  display: grid;
  gap: 14px;
`;

const Point = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;

  &:before {
    content: "✓";
    color: #22c55e;
    font-weight: 900;
  }
`;

/* ================= CTA ================= */

const CTA = styled.button`
  width: 100%;
  height: 56px;
  border-radius: 18px;
  background: linear-gradient(90deg, #22c55e, #3b82f6);
  border: none;
  color: #fff;
  font-size: 17px;
  font-weight: 1000;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

/* ================= COMPONENT ================= */

export default function StartJourneyModal({ open, onClose, onStart }) {
  return (
    <AnimatePresence>
      {open && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            initial={{ scale: 0.9, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Close onClick={onClose}>
              <X size={18} />
            </Close>

            <IconWrap>
              <Rocket size={34} />
            </IconWrap>

            <Title>
              Ready to start your
              <br />
              <Highlight>career journey?</Highlight>
            </Title>

            <Sub>
              You don’t need to figure everything out today.
              <br />
              You just need to take the <strong>first right step.</strong>
            </Sub>

            <Points>
              <Point>Clear direction based on you</Point>
              <Point>Daily tasks, not random learning</Point>
              <Point>Progress you can actually track</Point>
            </Points>

            <CTA onClick={onStart}>
              Start My Journey <ArrowRight size={18} />
            </CTA>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
}
